import axios from "axios";
import { getApiErrorMessage } from "@/lib/apiError";

const LOCAL_BACKEND_URL = "http://localhost:8000";
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").trim().replace(/\/+$/, "");

function getBackendUrl() {
    if (BACKEND_URL) {
        const invalidUrl =
            !/^https?:\/\//i.test(BACKEND_URL) ||
            BACKEND_URL.includes("placeholder") ||
            BACKEND_URL.includes("example.com");
        if (invalidUrl) {
            throw new Error("REACT_APP_BACKEND_URL must be an absolute EHS Render backend URL.");
        }
        return BACKEND_URL;
    }
    if (process.env.NODE_ENV !== "production") return LOCAL_BACKEND_URL;
    throw new Error("REACT_APP_BACKEND_URL must be configured with the EHS Render backend URL.");
}

export const API_BASE = `${getBackendUrl()}/api`;

const api = axios.create({ baseURL: API_BASE, withCredentials: true });

export function isNetworkError(err) {
    return (
        !err.response &&
        (err.code === "ERR_NETWORK" || err.message === "Network Error" || Boolean(err.request))
    );
}

function clearAuthState() {
    localStorage.removeItem("ehs_token");
    localStorage.removeItem("ehs_user");
}

function friendlyErrorMessage(status, fallback) {
    if (status === 401) return "Your session expired. Please sign in again.";
    if (status === 403) return "You do not have permission to perform that action.";
    if (status === 404) return "That record was not found or is not available to your role.";
    if (status === 429) return "Too many requests. Please wait a moment and try again.";
    if (status >= 500) return "Something went wrong on the server. Please try again.";
    return fallback || "Request failed. Please try again.";
}

api.interceptors.request.use((config) => {
    const token = localStorage.getItem("ehs_token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

api.interceptors.response.use(
    (r) => r,
    (err) => {
        const status = err.response?.status;
        const isSessionProbe = err.config?.url === "/auth/me";
        if (status === 401 && !isSessionProbe && window.location.pathname !== "/login") {
            clearAuthState();
            window.location.href = "/login";
        }
        if (isNetworkError(err)) {
            err.userMessage = "Unable to reach the EHS server. Please try again or contact an administrator.";
        } else {
            err.userMessage = friendlyErrorMessage(
                status,
                getApiErrorMessage(err, err.message)
            );
        }
        return Promise.reject(err);
    }
);

export { clearAuthState, friendlyErrorMessage };
export default api;
